import type { ApprovalBulkItemOutcome, ApprovalItem } from '../api/approvals.client.js';

export const BULK_APPROVAL_SAMPLE_LIMIT = 20;

export type BulkApprovalEntity = 'deal' | 'task' | 'note' | 'contact' | 'company';
export type BulkApprovalItemStatus = ApprovalBulkItemOutcome;
export type BulkApprovalRelationType =
  | 'deal'
  | 'company'
  | 'contact'
  | 'task'
  | 'calendar_event'
  | 'document'
  | 'template'
  | 'mixed';

export interface BulkApprovalSampleItem {
  ordinal: number;
  label: string | null;
  status: BulkApprovalItemStatus | null;
  relations: readonly BulkApprovalRelationType[];
}

export interface BulkApprovalSummary {
  entity: BulkApprovalEntity;
  operation:
    | 'create'
    | 'update'
    | 'delete'
    | 'stage'
    | 'owner'
    | 'tags'
    | 'status'
    | 'assignee'
    | 'due_date'
    | 'link'
    | 'unlink';
  count: number;
  countKnown: boolean;
  sample: readonly BulkApprovalSampleItem[];
  excluded: number;
  relationType: BulkApprovalRelationType | null;
  capped: boolean;
  downstreamImpact: readonly Readonly<{ key: DownstreamImpactKey; count: number }>[];
  result: {
    succeeded: number;
    failed: number;
    stale: number;
    skipped: number;
    unfinished: number;
  } | null;
}

export const DOWNSTREAM_IMPACT_KEYS = [
  'contacts',
  'deals',
  'tasks',
  'documents',
  'interactions',
  'notes',
  'calendarEvents',
  'calendarParticipants',
  'noteLinks',
  'attachments',
  'projectMembers',
] as const;
export type DownstreamImpactKey = (typeof DOWNSTREAM_IMPACT_KEYS)[number];

const TOOL = {
  bulk_create_deals: ['deal', 'create'],
  bulk_update_deals: ['deal', 'update'],
  bulk_delete_deals: ['deal', 'delete'],
  bulk_set_deal_stage: ['deal', 'stage'],
  bulk_set_deal_owner: ['deal', 'owner'],
  bulk_set_deal_tags: ['deal', 'tags'],
  bulk_create_tasks: ['task', 'create'],
  bulk_update_tasks: ['task', 'update'],
  bulk_delete_tasks: ['task', 'delete'],
  bulk_set_task_status: ['task', 'status'],
  bulk_set_task_assignee: ['task', 'assignee'],
  bulk_set_task_due_date: ['task', 'due_date'],
  bulk_create_notes: ['note', 'create'],
  bulk_update_notes: ['note', 'update'],
  bulk_delete_notes: ['note', 'delete'],
  bulk_link_notes: ['note', 'link'],
  bulk_unlink_notes: ['note', 'unlink'],
  bulk_create_contacts: ['contact', 'create'],
  bulk_update_contacts: ['contact', 'update'],
  bulk_delete_contacts: ['contact', 'delete'],
  bulk_set_contact_tags: ['contact', 'tags'],
  bulk_set_contact_owner: ['contact', 'owner'],
  bulk_link_contact_company: ['contact', 'link'],
  bulk_unlink_contact_company: ['contact', 'unlink'],
  bulk_create_companies: ['company', 'create'],
  bulk_update_companies: ['company', 'update'],
  bulk_delete_companies: ['company', 'delete'],
  bulk_set_company_tags: ['company', 'tags'],
  bulk_set_company_owner: ['company', 'owner'],
  bulk_link_companies: ['company', 'link'],
  bulk_unlink_companies: ['company', 'unlink'],
} as const;

function record(value: unknown): Readonly<Record<string, unknown>> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;
}

function safeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function safeText(value: unknown, limit = 80): string | null {
  if (typeof value !== 'string') return null;
  const normalized = Array.from(value)
    .map((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint <= 0x1f || codePoint === 0x7f ? ' ' : character;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return null;
  const characters = Array.from(normalized);
  return characters.length > limit ? `${characters.slice(0, limit - 1).join('')}…` : normalized;
}

function itemStatus(value: unknown): BulkApprovalItemStatus | null {
  return typeof value === 'string' &&
    ['pending', 'executing', 'succeeded', 'failed', 'stale', 'skipped'].includes(value)
    ? (value as BulkApprovalItemStatus)
    : null;
}

function relationType(value: unknown): BulkApprovalRelationType | null {
  return typeof value === 'string' &&
    ['deal', 'company', 'contact', 'task', 'calendar_event', 'document', 'template'].includes(value)
    ? (value as BulkApprovalRelationType)
    : null;
}

export function bulkApprovalItems(
  toolName: string,
  args: Readonly<Record<string, unknown>>,
): readonly unknown[] {
  const source = toolName.includes('create')
    ? args.proposals
    : toolName === 'bulk_link_companies' || toolName === 'bulk_unlink_companies'
      ? args.items
      : args.targets;
  return Array.isArray(source) ? source : [];
}

function companyItemsRelationType(items: readonly unknown[]): BulkApprovalRelationType | null {
  if (items.length === 0) return null;
  const relations = items.map((item) => relationType(record(item)?.relation));
  const first = relations[0] ?? null;
  return first !== null && relations.every((relation) => relation === first) ? first : 'mixed';
}

/**
 * Builds a deliberately redacted projection. Target IDs, record versions,
 * idempotency keys, digests and arbitrary payload/body fields never leave here.
 */
export function summarizeBulkApproval(approval: ApprovalItem): BulkApprovalSummary | null {
  const toolName = typeof approval.payload.toolName === 'string' ? approval.payload.toolName : '';
  const tool = TOOL[toolName as keyof typeof TOOL];
  if (!tool) return null;
  const [entity, operation] = tool;
  const args = record(approval.payload.args) ?? {};
  const source = bulkApprovalItems(toolName, args);
  const aggregate = approval.bulkExecutionAggregate;
  const result =
    aggregate?.version === 'partial_durable_v1' && aggregate.entity === entity ? aggregate : null;
  const total = safeNumber(result?.total);
  const preview = record(approval.payload.preview);
  const explicitCount = safeNumber(preview?.count);
  const previewItems =
    explicitCount !== null && Array.isArray(preview?.items) ? preview.items : null;
  const countKnown =
    explicitCount !== null ||
    total !== null ||
    Array.isArray(
      toolName.includes('create')
        ? args.proposals
        : toolName === 'bulk_link_companies' || toolName === 'bulk_unlink_companies'
          ? args.items
          : args.targets,
    );
  const count = previewItems !== null ? explicitCount! : (total ?? explicitCount ?? source.length);
  const resultSample = result?.sample ?? [];
  const sampleSource = previewItems ?? (resultSample.length > 0 ? resultSample : source);
  const sample = sampleSource.slice(0, BULK_APPROVAL_SAMPLE_LIMIT).map((raw, index) => {
    const item = record(raw) ?? {};
    const payload = record(item.payload) ?? {};
    const ordinal = safeNumber(item.ordinal) ?? index;
    return {
      ordinal,
      label:
        previewItems !== null
          ? safeText(item.title)
          : operation === 'create' && entity === 'note'
            ? safeText(payload.body, 120)
            : operation === 'create' && entity === 'contact'
              ? safeText(payload.firstName)
              : operation === 'create' && entity === 'company'
                ? safeText(payload.name)
                : operation === 'create'
                  ? safeText(payload.title)
                  : null,
      relations:
        entity === 'task' && operation === 'create'
          ? (['deal', 'contact', 'company'] as const).filter(
              (kind) => typeof payload[`${kind}Id`] === 'string',
            )
          : [],
      status: itemStatus(item.outcome),
    };
  });
  const excluded = safeNumber(record(approval.payload.preview)?.excluded) ?? 0;
  const relation = record(args.relation);
  const companyItemsRelation =
    toolName === 'bulk_link_companies' || toolName === 'bulk_unlink_companies'
      ? companyItemsRelationType(source)
      : null;
  const projectedRelationType =
    operation === 'link' || operation === 'unlink'
      ? (companyItemsRelation ??
        relationType(relation?.namespace ?? args.relationType ?? args.link))
      : null;
  const impactSource = record(record(approval.payload.preview)?.downstreamImpact);
  const downstreamImpact = impactSource
    ? DOWNSTREAM_IMPACT_KEYS.flatMap((key) => {
        const count = safeNumber(impactSource[key]);
        return count !== null && count > 0 ? [{ key, count }] : [];
      })
    : [];
  return {
    entity,
    operation,
    count,
    countKnown,
    sample,
    excluded,
    relationType: projectedRelationType,
    capped: count > BULK_APPROVAL_SAMPLE_LIMIT || sampleSource.length > BULK_APPROVAL_SAMPLE_LIMIT,
    downstreamImpact,
    result:
      result === null
        ? null
        : {
            succeeded: result.succeeded,
            failed: result.failed,
            stale: result.stale,
            skipped: result.skipped,
            unfinished: result.unfinished,
          },
  };
}
